import { useState, useEffect } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { translations, type Language } from '@/lib/translations';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';

const getInitialLang = (): Language => {
  try {
    const stored = localStorage.getItem('guestLanguage');
    if (stored === 'en' || stored === 'fr') return stored;
  } catch { /* ignore */ }
  return 'en';
};

export function ResetPassword() {
  const [lang] = useState<Language>(getInitialLang);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = searchParams.get('oobCode');

  const t = (key: string): string => {
    const keys = key.split('.');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let value: any = translations[lang];
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) value = value[k];
      else return key;
    }
    return typeof value === 'string' ? value : key;
  };

  // Verify the oobCode is valid on mount
  useEffect(() => {
    if (!oobCode) {
      setError('Invalid or missing reset link. Please request a new one.');
      setVerifying(false);
      return;
    }

    verifyPasswordResetCode(auth, oobCode)
      .then((userEmail) => {
        setEmail(userEmail);
        setVerifying(false);
      })
      .catch(() => {
        setError('This reset link is invalid or has expired. Please request a new one.');
        setVerifying(false);
      });
  }, [oobCode]);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      return setError(t('errors.passwordMismatch'));
    }
    if (password.length < 6) {
      return setError(t('errors.passwordTooShort'));
    }

    setLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode!, password);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      {/* Animated background blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500/30 rounded-full blur-3xl animate-float"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/30 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }}></div>
      </div>

      <Card className="w-full max-w-md relative z-10 animate-scale-in">

        {/* Logo and title */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 shadow-lg animate-float">
            <span className="text-3xl">💰</span>
          </div>
          <h1 className="mb-2 text-4xl font-bold gradient-text">
            {t('dashboard.title')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {t('auth.resetPassword')}
          </p>
        </div>

        {/* Verifying the link */}
        {verifying && (
          <div className="flex flex-col items-center gap-3 py-8">
            <svg className="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="text-sm text-slate-500 dark:text-slate-400">Verifying your reset link...</p>
          </div>
        )}

        {/* Invalid / expired link */}
        {!verifying && error && !success && (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <svg className="h-7 w-7 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="mb-6 text-sm text-slate-600 dark:text-slate-400">{error}</p>
            <Link
              to="/login"
              className="inline-block w-full text-center rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 text-sm font-semibold text-white shadow-md hover:opacity-90 transition-opacity"
            >
              {t('auth.backToSignIn')}
            </Link>
          </div>
        )}

        {/* Success */}
        {success && (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
              <svg className="h-7 w-7 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="mb-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
              Password updated!
            </h2>
            <p className="mb-6 text-sm text-slate-600 dark:text-slate-400">
              Your password has been reset. You can now sign in with your new password.
            </p>
            <Button variant="primary" fullWidth onClick={() => navigate('/login')}>
              {t('auth.signIn')}
            </Button>
          </div>
        )}

        {/* Reset form */}
        {!verifying && !error && !success && (
          <>
            {email && (
              <p className="mb-5 text-sm text-slate-600 dark:text-slate-400 text-center">
                Setting new password for{' '}
                <span className="font-medium text-slate-800 dark:text-slate-200">{email}</span>
              </p>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                type="password"
                label="New Password"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />

              <Input
                type="password"
                label="Confirm New Password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
              />

              {error && (
                <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4">
                  <p className="text-sm text-red-700 dark:text-red-400 font-medium">{error}</p>
                </div>
              )}

              <Button type="submit" variant="primary" fullWidth disabled={loading}>
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Updating password...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          </>
        )}

        {/* Unova Branding */}
        <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
          <p className="text-center text-xs text-slate-500 dark:text-slate-400">
            MyBalance by{' '}
            <span className="font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Unova
            </span>
          </p>
        </div>
      </Card>
    </div>
  );
}